import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, User, LogOut, Search, Filter, Plus, Trash2, Check, X, Globe, Upload, FileText, Menu, MessageCircle, Mic, MicOff, Send, Package, BarChart3, Users, Settings, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';
import './i18n';
import './App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prescription Preview Modal Component
const PrescriptionPreviewModal = ({ imageUrl, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const minZoom = 0.5;
  const maxZoom = 3;

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, maxZoom));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, minZoom));
  };

  const handleReset = () => {
    setZoom(1);
  };

  if (!imageUrl) return null;

  return (
    <div className="prescription-modal" onClick={onClose}>
      <div className="prescription-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="prescription-modal-header">
          <h3 className="font-bold text-lg">Ordonnance</h3>
          <div className="flex items-center gap-4">
            <div className="zoom-controls">
              <button onClick={handleZoomOut} className="zoom-btn" disabled={zoom <= minZoom}>−</button>
              <span className="text-sm font-medium px-2">{Math.round(zoom * 100)}%</span>
              <button onClick={handleZoomIn} className="zoom-btn" disabled={zoom >= maxZoom}>+</button>
              <button onClick={handleReset} className="zoom-btn text-sm">Reset</button>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="prescription-modal-body">
          <img 
            src={imageUrl} 
            alt="Ordonnance" 
            style={{ transform: `scale(${zoom})` }}
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Check if user has admin privileges
const isAdminRole = (role) => {
  return ['admin', 'order_manager', 'stock_manager', 'analyst'].includes(role);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setUser(res.data);
      }).catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Navbar Component
const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (user) {
      axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        setCartCount(res.data.items?.length || 0);
      }).catch(() => {});
    }
  }, [user]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <nav className="glassmorphism-nav sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <span className="font-bold text-xl text-teal-500">pharmacie.cd</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="desktop-nav">
            <button 
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-slate-400 hover:text-teal-500 font-medium transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-5 h-5" />
              <span className="text-sm">{i18n.language === 'en' ? 'FR' : 'EN'}</span>
            </button>
            <Link to="/catalog" className="text-slate-400 hover:text-teal-500 font-medium transition-colors">
              {t('catalog')}
            </Link>
            {user ? (
              <>
                <Link to="/orders" className="text-slate-400 hover:text-teal-500 font-medium transition-colors">
                  {t('orders')}
                </Link>
                {isAdminRole(user.role) && (
                  <Link to="/admin" className="text-slate-400 hover:text-teal-500 font-medium transition-colors">
                    {t('admin')}
                  </Link>
                )}
                <Link to="/cart" className="relative text-slate-400 hover:text-teal-500 transition-colors" data-testid="cart-link">
                  <ShoppingCart className="w-6 h-6" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
                <button onClick={logout} className="flex items-center space-x-2 text-slate-400 hover:text-teal-500 transition-colors" data-testid="logout-button">
                  <User className="w-5 h-5" />
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-slate-400 hover:text-teal-500 font-medium transition-colors" data-testid="login-link">
                  {t('login')}
                </Link>
                <Link to="/register" className="btn-primary" data-testid="register-link">
                  {t('register')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className="mobile-menu"
        style={{ display: mobileMenuOpen ? 'flex' : 'none' }}
      >
        <button onClick={toggleLanguage} className="justify-center">
          <Globe className="w-5 h-5" />
          <span>{i18n.language === 'en' ? 'Français' : 'English'}</span>
        </button>
        <Link to="/catalog" onClick={closeMobileMenu}>
          <Search className="w-5 h-5" />
          <span>{t('catalog')}</span>
        </Link>
        {user ? (
          <>
            <Link to="/cart" onClick={closeMobileMenu}>
              <ShoppingCart className="w-5 h-5" />
              <span>{t('cart.title')} {cartCount > 0 && `(${cartCount})`}</span>
            </Link>
            <Link to="/orders" onClick={closeMobileMenu}>
              <Package className="w-5 h-5" />
              <span>{t('orders')}</span>
            </Link>
            {isAdminRole(user.role) && (
              <Link to="/admin" onClick={closeMobileMenu}>
                <Settings className="w-5 h-5" />
                <span>{t('admin')}</span>
              </Link>
            )}
            <button onClick={() => { logout(); closeMobileMenu(); }} className="text-rose-400">
              <LogOut className="w-5 h-5" />
              <span>{t('logout')}</span>
            </button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={closeMobileMenu}>
              <User className="w-5 h-5" />
              <span>{t('login')}</span>
            </Link>
            <Link to="/register" onClick={closeMobileMenu} className="!bg-teal-600">
              <Plus className="w-5 h-5" />
              <span>{t('register')}</span>
            </Link>
          </>
        )}
      </div>
    </nav>
  );
};

// Home Page
const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen">
      <div className="gradient-hero py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
              {t('home.title')}
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('home.subtitle')}
            </p>
            <div className="flex justify-center gap-4">
              <button onClick={() => navigate('/catalog')} className="btn-primary text-lg px-8 py-4" data-testid="browse-medicines-btn">
                {t('home.browse')}
              </button>
              <button onClick={() => navigate('/register')} className="btn-secondary text-lg px-8 py-4">
                {t('home.getStarted')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-xl hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-teal-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Check className="w-8 h-8 text-teal-700" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('home.feature1.title')}</h3>
              <p className="text-slate-600">{t('home.feature1.desc')}</p>
            </div>
            <div className="text-center p-8 rounded-xl hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-teal-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <ShoppingCart className="w-8 h-8 text-teal-700" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('home.feature2.title')}</h3>
              <p className="text-slate-600">{t('home.feature2.desc')}</p>
            </div>
            <div className="text-center p-8 rounded-xl hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-teal-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <User className="w-8 h-8 text-teal-700" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('home.feature3.title')}</h3>
              <p className="text-slate-600">{t('home.feature3.desc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Page
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      login(res.data.token, res.data.user);
      toast.success(t('auth.loginSuccess'));
      navigate('/catalog');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-slate-100 py-12 px-4">
      <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-md">
        <h2 className="text-3xl font-bold text-center mb-6 text-slate-900">{t('auth.welcomeBack')}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              required
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              required
              data-testid="login-password-input"
            />
          </div>
          <button type="submit" className="w-full btn-primary py-3" data-testid="login-submit-btn">
            {t('auth.loginBtn')}
          </button>
        </form>
        <p className="text-center mt-4 text-slate-600">
          {t('auth.noAccount')} <Link to="/register" className="text-teal-700 font-medium hover:underline">{t('register')}</Link>
        </p>
      </div>
    </div>
  );
};

// Register Page
const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [sameAsPhone, setSameAsPhone] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/register`, { 
        email, 
        password, 
        full_name: fullName,
        phone,
        whatsapp: sameAsPhone ? null : whatsapp
      });
      login(res.data.token, res.data.user);
      toast.success(t('auth.registerSuccess'));
      navigate('/catalog');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registerFailed'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-slate-100 py-12 px-4">
      <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-md">
        <h2 className="text-3xl font-bold text-center mb-6 text-slate-900">{t('auth.createAccount')}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('auth.fullName')}</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              required
              data-testid="register-fullname-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              required
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('auth.phone')}</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+243 XXX XXX XXX"
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              required
              data-testid="register-phone-input"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="sameAsPhone"
                checked={sameAsPhone}
                onChange={(e) => setSameAsPhone(e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded"
                data-testid="same-as-phone-checkbox"
              />
              <label htmlFor="sameAsPhone" className="text-sm text-slate-700">{t('auth.whatsappSameAsPhone')}</label>
            </div>
            {!sameAsPhone && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('auth.whatsapp')}</label>
                <input
                  type="tel"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="+243 XXX XXX XXX"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
                  data-testid="register-whatsapp-input"
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
              required
              data-testid="register-password-input"
            />
          </div>
          <button type="submit" className="w-full btn-primary py-3" data-testid="register-submit-btn">
            {t('auth.registerBtn')}
          </button>
        </form>
        <p className="text-center mt-4 text-slate-600">
          {t('auth.hasAccount')} <Link to="/login" className="text-teal-700 font-medium hover:underline">{t('login')}</Link>
        </p>
      </div>
    </div>
  );
};

// Catalog Page
const Catalog = () => {
  const [medicines, setMedicines] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [symptomSearch, setSymptomSearch] = useState('');
  const [filteredSymptoms, setFilteredSymptoms] = useState([]);
  const [showSymptomDropdown, setShowSymptomDropdown] = useState(false);
  const [searchMode, setSearchMode] = useState('name'); // 'name' or 'symptom'
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [ageGroups, setAgeGroups] = useState([]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCategories();
    fetchSymptoms();
    fetchForms();
    fetchAgeGroups();
  }, []);

  useEffect(() => {
    if (searchMode === 'name') {
      fetchMedicines();
    }
  }, [search, category, searchMode, selectedForm, selectedAgeGroup]);

  useEffect(() => {
    // Filter symptoms based on input
    if (symptomSearch.length > 0) {
      const filtered = symptoms.filter(s => 
        s.toLowerCase().includes(symptomSearch.toLowerCase())
      ).slice(0, 8);
      setFilteredSymptoms(filtered);
      setShowSymptomDropdown(true);
    } else {
      setFilteredSymptoms([]);
      setShowSymptomDropdown(false);
    }
  }, [symptomSearch, symptoms]);

  const fetchMedicines = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (selectedForm) params.append('form', selectedForm);
      if (selectedAgeGroup) params.append('age_group', selectedAgeGroup);
      const res = await axios.get(`${API}/medicines?${params}`);
      setMedicines(res.data);
    } catch (error) {
      toast.error(t('catalog.loadFailed'));
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/medicines/categories/list`);
      setCategories(res.data);
    } catch (error) {}
  };

  const fetchForms = async () => {
    try {
      const res = await axios.get(`${API}/medicines/forms/list`);
      setForms(res.data);
    } catch (error) {}
  };

  const fetchAgeGroups = async () => {
    try {
      const res = await axios.get(`${API}/medicines/age-groups/list`);
      setAgeGroups(res.data);
    } catch (error) {}
  };

  const fetchSymptoms = async () => {
    try {
      const res = await axios.get(`${API}/medicines/symptoms/list`);
      setSymptoms(res.data);
    } catch (error) {}
  };

  const searchBySymptom = async (symptom) => {
    try {
      setSymptomSearch(symptom);
      setShowSymptomDropdown(false);
      const res = await axios.get(`${API}/medicines/search/symptoms?symptom=${encodeURIComponent(symptom)}`);
      setMedicines(res.data);
    } catch (error) {
      toast.error(t('catalog.loadFailed'));
    }
  };

  const addToCart = async (medicineId) => {
    if (!user) {
      toast.error(t('catalog.loginRequired'));
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/cart/add`, { medicine_id: medicineId, quantity: 1 }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('catalog.addedToCart'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const clearSymptomSearch = () => {
    setSymptomSearch('');
    setSearchMode('name');
    fetchMedicines();
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-4 text-slate-900">{t('catalog.title')}</h1>
        
        {/* Search Mode Toggle */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg bg-slate-200 p-1">
            <button
              onClick={() => { setSearchMode('name'); setSymptomSearch(''); fetchMedicines(); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                searchMode === 'name' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="search-mode-name"
            >
              {t('catalog.searchByName')}
            </button>
            <button
              onClick={() => { setSearchMode('symptom'); setSearch(''); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                searchMode === 'symptom' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="search-mode-symptom"
            >
              {t('catalog.searchBySymptom')}
            </button>
          </div>
        </div>
        
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          {searchMode === 'name' ? (
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('catalog.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                data-testid="search-input"
              />
            </div>
          ) : (
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-rose-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('catalog.symptomPlaceholder')}
                value={symptomSearch}
                onChange={(e) => setSymptomSearch(e.target.value)}
                onFocus={() => symptomSearch.length > 0 && setShowSymptomDropdown(true)}
                className="w-full pl-12 pr-12 py-3 border border-rose-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none bg-rose-50"
                data-testid="symptom-search-input"
              />
              {symptomSearch && (
                <button
                  onClick={clearSymptomSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              
              {/* Symptom Suggestions Dropdown */}
              {showSymptomDropdown && filteredSymptoms.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 max-h-64 overflow-y-auto">
                  {filteredSymptoms.map((symptom, idx) => (
                    <button
                      key={idx}
                      onClick={() => searchBySymptom(symptom)}
                      className="w-full text-left px-4 py-3 hover:bg-rose-50 border-b border-slate-100 last:border-b-0 transition-colors"
                      data-testid={`symptom-suggestion-${idx}`}
                    >
                      <span className="text-rose-700 font-medium">{symptom}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            data-testid="category-filter"
          >
            <option value="">{t('catalog.allCategories')}</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 border rounded-xl flex items-center gap-2 transition-colors ${
              showFilters || selectedForm || selectedAgeGroup 
                ? 'border-teal-500 bg-teal-50 text-teal-700' 
                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="toggle-filters"
          >
            <Filter className="w-5 h-5" />
            {t('catalog.moreFilters')}
            {(selectedForm || selectedAgeGroup) && (
              <span className="bg-teal-500 text-white text-xs px-2 py-0.5 rounded-full">
                {[selectedForm, selectedAgeGroup].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>
        
        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="mb-6 p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('catalog.pharmaceuticalForm')}
                </label>
                <select
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  data-testid="form-filter"
                >
                  <option value="">{t('catalog.allForms')}</option>
                  {forms.map(form => (
                    <option key={form} value={form}>{form.charAt(0).toUpperCase() + form.slice(1)}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('catalog.ageGroup')}
                </label>
                <select
                  value={selectedAgeGroup}
                  onChange={(e) => setSelectedAgeGroup(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  data-testid="age-group-filter"
                >
                  <option value="">{t('catalog.allAgeGroups')}</option>
                  {ageGroups.map(ag => (
                    <option key={ag} value={ag}>{ag === 'adulte' ? t('catalog.adult') : ag === 'enfant' ? t('catalog.child') : t('catalog.all')}</option>
                  ))}
                </select>
              </div>
              
              {(selectedForm || selectedAgeGroup) && (
                <div className="flex items-end">
                  <button
                    onClick={() => { setSelectedForm(''); setSelectedAgeGroup(''); }}
                    className="px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                    data-testid="clear-filters"
                  >
                    {t('catalog.clearFilters')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results info */}
        {symptomSearch && searchMode === 'symptom' && (
          <div className="mb-6 bg-rose-50 border border-rose-200 rounded-xl p-4">
            <p className="text-rose-800">
              <span className="font-medium">{medicines.length}</span> {t('catalog.medicinesFor')} "<span className="font-bold">{symptomSearch}</span>"
            </p>
          </div>
        )}

        {/* Popular Symptoms Quick Access */}
        {searchMode === 'symptom' && !symptomSearch && (
          <div className="mb-8">
            <p className="text-sm text-slate-600 mb-3">{t('catalog.commonSymptoms')}:</p>
            <div className="flex flex-wrap gap-2">
              {['fièvre', 'douleur', 'infection', 'paludisme', 'toux', 'diarrhée', 'hypertension', 'diabète'].map(symptom => (
                <button
                  key={symptom}
                  onClick={() => searchBySymptom(symptom)}
                  className="px-4 py-2 bg-white border border-rose-200 rounded-full text-sm text-rose-700 hover:bg-rose-50 hover:border-rose-300 transition-colors"
                  data-testid={`quick-symptom-${symptom}`}
                >
                  {symptom}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {medicines.map(medicine => (
            <div key={medicine.id} className="medicine-card bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`medicine-card-${medicine.id}`}>
              <div className="relative">
                <img src={medicine.image_url} alt={medicine.name} className="w-full h-48 object-cover" />
                {medicine.age_group && medicine.age_group !== 'tous' && (
                  <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full font-medium ${
                    medicine.age_group === 'enfant' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {medicine.age_group === 'enfant' ? t('catalog.forChildren') : t('catalog.forAdults')}
                  </span>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-bold text-lg mb-2 text-slate-900">{medicine.name}</h3>
                <p className="text-sm text-slate-600 mb-2 line-clamp-2">{medicine.description}</p>
                
                {/* Form badge */}
                {medicine.form && (
                  <span className="inline-block text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full mb-2">
                    {medicine.form.charAt(0).toUpperCase() + medicine.form.slice(1)}
                  </span>
                )}
                
                {/* Show symptoms badges if available */}
                {medicine.symptoms && medicine.symptoms.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {medicine.symptoms.slice(0, 3).map((symptom, idx) => (
                      <span key={idx} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">
                        {symptom}
                      </span>
                    ))}
                    {medicine.symptoms.length > 3 && (
                      <span className="text-xs text-slate-400">+{medicine.symptoms.length - 3}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold text-teal-700">${medicine.price}</span>
                  {medicine.requires_prescription && (
                    <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full">{t('catalog.rxRequired')}</span>
                  )}
                </div>
                <button
                  onClick={() => addToCart(medicine.id)}
                  className="w-full btn-primary py-2 text-sm"
                  data-testid={`add-to-cart-${medicine.id}`}
                >
                  {t('catalog.addToCart')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {medicines.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500 text-lg">{t('catalog.noResults')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Cart Page
const Cart = () => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [isDifferentBuyer, setIsDifferentBuyer] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [buyerWhatsapp, setBuyerWhatsapp] = useState('');
  const [buyerWhatsappSame, setBuyerWhatsappSame] = useState(true);
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const res = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(res.data);
    } catch (error) {
      toast.error(t('cart.loadFailed'));
    }
  };

  const removeItem = async (medicineId) => {
    try {
      await axios.delete(`${API}/cart/remove/${medicineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('cart.itemRemoved'));
      fetchCart();
    } catch (error) {
      toast.error(t('cart.removeFailed'));
    }
  };

  const checkout = async (paymentMethod) => {
    // Validate buyer info if different buyer
    if (isDifferentBuyer) {
      if (!buyerName.trim()) {
        toast.error(t('cart.buyerNameRequired'));
        return;
      }
      if (!buyerPhone.trim()) {
        toast.error(t('cart.buyerPhoneRequired'));
        return;
      }
    }

    try {
      const orderData = {
        payment_method: paymentMethod,
        is_different_buyer: isDifferentBuyer,
        buyer_name: isDifferentBuyer ? buyerName : null,
        buyer_phone: isDifferentBuyer ? buyerPhone : null,
        buyer_whatsapp: isDifferentBuyer ? (buyerWhatsappSame ? buyerPhone : buyerWhatsapp) : null
      };

      const res = await axios.post(`${API}/orders/create`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Commande créée!');
      navigate(`/checkout?order_id=${res.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la commande');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8 text-slate-900">{t('cart.title')}</h1>
        
        {cart.items.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <ShoppingCart className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-xl text-slate-600 mb-4">{t('cart.empty')}</p>
            <button onClick={() => navigate('/catalog')} className="btn-primary">
              {t('cart.continueShopping')}
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm divide-y">
              {cart.items.map(item => (
                <div key={item.medicine_id} className="p-6 flex items-center justify-between" data-testid={`cart-item-${item.medicine_id}`}>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{item.medicine_name}</h3>
                    <p className="text-slate-600">{t('cart.quantity')}: {item.quantity}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xl font-bold text-teal-700">${(item.price * item.quantity).toFixed(2)}</span>
                    <button
                      onClick={() => removeItem(item.medicine_id)}
                      className="text-rose-500 hover:text-rose-700"
                      data-testid={`remove-item-${item.medicine_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Buyer Info Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="differentBuyer"
                  checked={isDifferentBuyer}
                  onChange={(e) => setIsDifferentBuyer(e.target.checked)}
                  className="w-5 h-5 text-teal-600 rounded"
                  data-testid="different-buyer-checkbox"
                />
                <label htmlFor="differentBuyer" className="text-slate-700 font-medium">
                  {t('cart.differentBuyer')}
                </label>
              </div>

              {isDifferentBuyer && (
                <div className={`buyer-info-section ${isDifferentBuyer ? 'active' : ''}`}>
                  <h3 className="font-bold text-lg mb-4 text-teal-700">{t('cart.buyerInfo')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t('cart.buyerName')}</label>
                      <input
                        type="text"
                        value={buyerName}
                        onChange={(e) => setBuyerName(e.target.value)}
                        placeholder={t('cart.buyerNamePlaceholder')}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl"
                        data-testid="buyer-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t('cart.buyerPhone')}</label>
                      <input
                        type="tel"
                        value={buyerPhone}
                        onChange={(e) => setBuyerPhone(e.target.value)}
                        placeholder="+243 XXX XXX XXX"
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl"
                        data-testid="buyer-phone-input"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="buyerWhatsappSame"
                          checked={buyerWhatsappSame}
                          onChange={(e) => setBuyerWhatsappSame(e.target.checked)}
                          className="w-4 h-4 text-teal-600 rounded"
                        />
                        <label htmlFor="buyerWhatsappSame" className="text-sm text-slate-700">{t('cart.whatsappSameAsPhone')}</label>
                      </div>
                      {!buyerWhatsappSame && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t('cart.buyerWhatsapp')}</label>
                          <input
                            type="tel"
                            value={buyerWhatsapp}
                            onChange={(e) => setBuyerWhatsapp(e.target.value)}
                            placeholder="+243 XXX XXX XXX"
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl"
                            data-testid="buyer-whatsapp-input"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
              <div className="flex justify-between items-center mb-6">
                <span className="text-2xl font-bold">{t('cart.total')}:</span>
                <span className="text-3xl font-bold text-teal-700">${cart.total?.toFixed(2)}</span>
              </div>
              <div className="space-y-3">
                <button onClick={() => checkout('mock')} className="w-full btn-primary py-3" data-testid="checkout-mock-btn">
                  {t('cart.checkoutMock')}
                </button>
                <button onClick={() => checkout('stripe')} className="w-full btn-accent py-3" data-testid="checkout-stripe-btn">
                  {t('cart.checkoutStripe')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Prescription Upload Component
const PrescriptionUpload = ({ orderId, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);
  const { token } = useAuth();
  const { t } = useTranslation();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, or PDF');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size too large. Maximum 5MB allowed');
      return;
    }

    setFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/prescriptions/upload/${orderId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(t('prescription.uploaded'));
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('prescription.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-6 h-6 text-rose-500" />
        <h2 className="text-2xl font-bold text-slate-900">{t('prescription.title')}</h2>
      </div>
      
      <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 mb-6">
        <p className="text-rose-700 text-sm">
          <span className="font-semibold">{t('prescription.required')}: </span>
          {t('prescription.message')}
        </p>
      </div>

      {!file ? (
        <div
          className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
            dragActive ? 'border-teal-500 bg-teal-50' : 'border-slate-300 hover:border-teal-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          data-testid="prescription-dropzone"
        >
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,application/pdf"
            onChange={handleChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="prescription-file-input"
          />
          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-lg font-medium text-slate-700 mb-2">{t('prescription.dragDrop')}</p>
          <p className="text-sm text-slate-500">{t('prescription.fileTypes')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {preview && (
            <div className="border border-slate-200 rounded-lg p-4">
              <img src={preview} alt="Prescription preview" className="max-h-64 mx-auto rounded" />
            </div>
          )}
          
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-teal-700" />
                <div>
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-600">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                }}
                className="text-rose-500 hover:text-rose-700"
                data-testid="remove-prescription-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <button
            onClick={handleUpload}
            disabled={uploading}
            className={`w-full btn-primary py-3 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="upload-prescription-btn"
          >
            {uploading ? t('prescription.uploading') : t('prescription.uploadBtn')}
          </button>
        </div>
      )}
    </div>
  );
};

// Checkout Page
const Checkout = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id');
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [showPrescriptionUpload, setShowPrescriptionUpload] = useState(false);
  const { token } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [orderId, sessionId]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(res.data);
      
      // Check if prescription is required and not uploaded
      if (res.data.requires_prescription && !res.data.prescription_url) {
        setShowPrescriptionUpload(true);
      }
    } catch (error) {
      toast.error('Failed to load order');
    }
  };

  const checkPaymentStatus = async () => {
    if (!sessionId) return;
    
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentStatus(res.data.status);
      
      if (res.data.status === 'paid') {
        toast.success(t('checkout.paymentSuccess'));
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
    }
  };

  const handlePrescriptionUploadSuccess = () => {
    setShowPrescriptionUpload(false);
    fetchOrder();
  };

  const handlePayment = async () => {
    // Check if prescription is required but not uploaded
    if (order.requires_prescription && !order.prescription_url) {
      toast.error(t('prescription.required'));
      setShowPrescriptionUpload(true);
      return;
    }

    try {
      const res = await axios.post(`${API}/payments/checkout?order_id=${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.type === 'mock') {
        toast.success(t('checkout.paymentSuccess'));
        setPaymentStatus('paid');
        fetchOrder();
      } else if (res.data.type === 'stripe') {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(t('checkout.paymentFailed'));
    }
  };

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">{t('checkout.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8 text-slate-900">{t('checkout.title')}</h1>
        
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">{t('checkout.orderSummary')}</h2>
          <div className="space-y-3 mb-6">
            {order.items.map(item => (
              <div key={item.medicine_id} className="flex justify-between">
                <span>{item.medicine_name} x {item.quantity}</span>
                <span className="font-bold">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-4 flex justify-between text-xl font-bold">
            <span>{t('cart.total')}:</span>
            <span className="text-teal-700">${order.total_amount.toFixed(2)}</span>
          </div>
        </div>

        {/* Prescription Upload Section */}
        {showPrescriptionUpload && (
          <div className="mb-6" data-testid="prescription-upload-section">
            <PrescriptionUpload 
              orderId={orderId} 
              onUploadSuccess={handlePrescriptionUploadSuccess}
            />
          </div>
        )}

        {/* Show prescription status if uploaded */}
        {order.requires_prescription && order.prescription_url && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-5 h-5 text-teal-700" />
              <p className="font-semibold text-teal-900">{t('prescription.uploaded')}</p>
            </div>
            <a 
              href={order.prescription_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-teal-700 hover:underline text-sm"
              data-testid="view-prescription-link"
            >
              {t('prescription.viewPrescription')}
            </a>
          </div>
        )}

        {order.payment_status === 'pending' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">{t('checkout.completePayment')}</h2>
            {order.requires_prescription && !order.prescription_url && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 mb-4">
                <p className="text-rose-700 text-sm">
                  {t('prescription.message')}
                </p>
              </div>
            )}
            <button 
              onClick={handlePayment} 
              className="w-full btn-accent py-3"
              disabled={order.requires_prescription && !order.prescription_url}
              data-testid="pay-now-btn"
            >
              {t('checkout.payNow')}
            </button>
          </div>
        )}

        {(order.payment_status === 'paid' || paymentStatus === 'paid') && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 text-center">
            <Check className="w-16 h-16 text-teal-700 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-teal-900 mb-2">{t('checkout.paymentSuccess')}</h2>
            <p className="text-teal-700 mb-4">{t('checkout.orderConfirmed')}</p>
            <Link to="/orders" className="btn-primary">
              {t('checkout.viewOrders')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Orders Page
const Orders = () => {
  const [orders, setOrders] = useState([]);
  const { token } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch (error) {
      toast.error(t('orders.loadFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8 text-slate-900">{t('orders.title')}</h1>
        
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="text-xl text-slate-600">{t('orders.noOrders')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-xl shadow-sm p-6" data-testid={`order-${order.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-slate-600">{t('orders.orderId')}: {order.id}</p>
                    <p className="text-sm text-slate-600">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      order.payment_status === 'paid' ? 'bg-teal-100 text-teal-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {t(`payment.${order.payment_status}`)}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  {order.items.map(item => (
                    <div key={item.medicine_id} className="flex justify-between text-sm">
                      <span>{item.medicine_name} x {item.quantity}</span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4 flex justify-between font-bold">
                  <span>{t('cart.total')}:</span>
                  <span className="text-teal-700">${order.total_amount.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [stats, setStats] = useState({ total_orders: 0, total_users: 0, total_medicines: 0, total_revenue: 0 });
  const [orders, setOrders] = useState([]);
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchOrders();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      toast.error(t('admin.loadStatsFailed'));
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch (error) {
      toast.error(t('admin.loadOrdersFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-6 text-slate-900">{t('admin.title')}</h1>
        
        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button 
            onClick={() => navigate('/admin/orders')}
            className="bg-white rounded-xl p-6 text-left hover:shadow-md transition-all border border-slate-200 hover:border-teal-500"
            data-testid="admin-orders-link"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">Commandes</p>
                <p className="text-sm text-slate-500">{stats.total_orders} commandes</p>
              </div>
            </div>
          </button>
          <button 
            onClick={() => navigate('/admin/analytics')}
            className="bg-white rounded-xl p-6 text-left hover:shadow-md transition-all border border-slate-200 hover:border-teal-500"
            data-testid="admin-analytics-link"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">Analytics</p>
                <p className="text-sm text-slate-500">${stats.total_revenue.toFixed(0)} revenus</p>
              </div>
            </div>
          </button>
          <button 
            onClick={() => navigate('/admin/medicines')}
            className="bg-white rounded-xl p-6 text-left hover:shadow-md transition-all border border-slate-200 hover:border-teal-500"
            data-testid="admin-medicines-link"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">{t('admin.manageMedicines')}</p>
                <p className="text-sm text-slate-500">{stats.total_medicines} médicaments</p>
              </div>
            </div>
          </button>
          <button 
            onClick={() => navigate('/admin/users')}
            className="bg-white rounded-xl p-6 text-left hover:shadow-md transition-all border border-slate-200 hover:border-teal-500"
            data-testid="admin-users-link"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">{t('admin.manageUsers')}</p>
                <p className="text-sm text-slate-500">{stats.total_users} utilisateurs</p>
              </div>
            </div>
          </button>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card">
            <p className="text-xs text-slate-500 mb-1">{t('admin.totalOrders')}</p>
            <p className="text-2xl font-bold text-teal-600">{stats.total_orders}</p>
          </div>
          <div className="stat-card">
            <p className="text-xs text-slate-500 mb-1">{t('admin.totalUsers')}</p>
            <p className="text-2xl font-bold text-teal-600">{stats.total_users}</p>
          </div>
          <div className="stat-card">
            <p className="text-xs text-slate-500 mb-1">{t('admin.totalMedicines')}</p>
            <p className="text-2xl font-bold text-teal-600">{stats.total_medicines}</p>
          </div>
          <div className="stat-card">
            <p className="text-xs text-slate-500 mb-1">{t('admin.totalRevenue')}</p>
            <p className="text-2xl font-bold text-teal-600">${stats.total_revenue.toFixed(2)}</p>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
          <h2 className="text-xl font-bold mb-4 text-slate-900">{t('admin.recentOrders')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-slate-600">{t('orders.orderId')}</th>
                  <th className="text-left py-2 px-3 text-slate-600">{t('admin.user')}</th>
                  <th className="text-left py-2 px-3 text-slate-600">{t('admin.date')}</th>
                  <th className="text-left py-2 px-3 text-slate-600">{t('cart.total')}</th>
                  <th className="text-left py-2 px-3 text-slate-600">{t('admin.payment')}</th>
                  <th className="text-left py-2 px-3 text-slate-600">{t('admin.prescriptionStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 10).map(order => (
                  <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-mono text-xs text-slate-700">{order.id.substring(0, 8)}...</td>
                    <td className="py-2 px-3 text-slate-700">{order.user_email}</td>
                    <td className="py-2 px-3 text-slate-700">{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="py-2 px-3 font-medium text-slate-900">${order.total_amount.toFixed(2)}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.payment_status === 'paid' ? 'bg-teal-100 text-teal-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {t(`payment.${order.payment_status}`)}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      {order.requires_prescription ? (
                        order.prescription_url ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-teal-100 text-teal-700">Reçue</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-rose-100 text-rose-700">Requise</span>
                        )
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && (
              <p className="text-center py-8 text-slate-500">Aucune commande pour le moment</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Medicines Management
const AdminMedicines = () => {
  const [medicines, setMedicines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', category: '', brand: '',
    stock_quantity: '', image_url: '', requires_prescription: false
  });
  const { token } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchMedicines();
    fetchCategories();
  }, [search, categoryFilter]);

  const fetchMedicines = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      const res = await axios.get(`${API}/medicines?${params}`);
      setMedicines(res.data);
    } catch (error) {
      toast.error('Échec du chargement des médicaments');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/medicines/categories/list`);
      setCategories(res.data);
    } catch (error) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock_quantity: parseInt(formData.stock_quantity)
      };

      if (editingMedicine) {
        await axios.put(`${API}/medicines/${editingMedicine.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('admin.medicineUpdated'));
      } else {
        await axios.post(`${API}/medicines`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('admin.medicineAdded'));
      }
      
      setShowForm(false);
      setEditingMedicine(null);
      resetForm();
      fetchMedicines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (medicine) => {
    setEditingMedicine(medicine);
    setFormData({
      name: medicine.name,
      description: medicine.description,
      price: medicine.price.toString(),
      category: medicine.category,
      brand: medicine.brand,
      stock_quantity: medicine.stock_quantity.toString(),
      image_url: medicine.image_url || '',
      requires_prescription: medicine.requires_prescription
    });
    setShowForm(true);
  };

  const handleDelete = async (medicineId) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API}/medicines/${medicineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin.medicineDeleted'));
      fetchMedicines();
    } catch (error) {
      toast.error('Échec de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', price: '', category: '', brand: '',
      stock_quantity: '', image_url: '', requires_prescription: false
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Link to="/admin" className="text-teal-700 hover:underline text-sm mb-2 inline-block">&larr; Retour au tableau de bord</Link>
            <h1 className="text-3xl font-bold text-slate-900">{t('admin.manageMedicines')}</h1>
          </div>
          <button 
            onClick={() => { setShowForm(true); setEditingMedicine(null); resetForm(); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-medicine-btn"
          >
            <Plus className="w-5 h-5" /> {t('admin.addMedicine')}
          </button>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                data-testid="admin-medicine-search"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              data-testid="admin-category-filter"
            >
              <option value="">{t('catalog.allCategories')}</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Medicine Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">
                    {editingMedicine ? t('admin.editMedicine') : t('admin.addMedicine')}
                  </h2>
                  <button onClick={() => { setShowForm(false); setEditingMedicine(null); }} className="text-slate-500 hover:text-slate-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.medicineName')}</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                        required
                        data-testid="medicine-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.brand')}</label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData({...formData, brand: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                        required
                        data-testid="medicine-brand-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                      rows="3"
                      required
                      data-testid="medicine-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.price')} ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                        required
                        data-testid="medicine-price-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.category')}</label>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                        required
                        list="categories-list"
                        data-testid="medicine-category-input"
                      />
                      <datalist id="categories-list">
                        {categories.map(cat => (
                          <option key={cat} value={cat} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.stock')}</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.stock_quantity}
                        onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                        required
                        data-testid="medicine-stock-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.imageUrl')}</label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                      placeholder="https://..."
                      data-testid="medicine-image-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="requires_prescription"
                      checked={formData.requires_prescription}
                      onChange={(e) => setFormData({...formData, requires_prescription: e.target.checked})}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                      data-testid="medicine-prescription-checkbox"
                    />
                    <label htmlFor="requires_prescription" className="text-sm text-slate-700">
                      {t('admin.requiresPrescription')}
                    </label>
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button type="submit" className="flex-1 btn-primary py-2" data-testid="save-medicine-btn">
                      {t('admin.save')}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => { setShowForm(false); setEditingMedicine(null); }}
                      className="flex-1 btn-secondary py-2"
                    >
                      {t('admin.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Medicines Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4">{t('admin.medicineName')}</th>
                  <th className="text-left py-3 px-4">{t('admin.category')}</th>
                  <th className="text-left py-3 px-4">{t('admin.brand')}</th>
                  <th className="text-left py-3 px-4">{t('admin.price')}</th>
                  <th className="text-left py-3 px-4">{t('admin.stock')}</th>
                  <th className="text-left py-3 px-4">Rx</th>
                  <th className="text-left py-3 px-4">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {medicines.map(medicine => (
                  <tr key={medicine.id} className="border-b hover:bg-slate-50" data-testid={`admin-medicine-row-${medicine.id}`}>
                    <td className="py-3 px-4 font-medium">{medicine.name}</td>
                    <td className="py-3 px-4">{medicine.category}</td>
                    <td className="py-3 px-4">{medicine.brand}</td>
                    <td className="py-3 px-4">${medicine.price.toFixed(2)}</td>
                    <td className="py-3 px-4">{medicine.stock_quantity}</td>
                    <td className="py-3 px-4">
                      {medicine.requires_prescription ? (
                        <Check className="w-5 h-5 text-rose-500" />
                      ) : (
                        <X className="w-5 h-5 text-slate-300" />
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(medicine)}
                          className="text-teal-700 hover:text-teal-900"
                          data-testid={`edit-medicine-${medicine.id}`}
                        >
                          {t('admin.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(medicine.id)}
                          className="text-rose-600 hover:text-rose-800"
                          data-testid={`delete-medicine-${medicine.id}`}
                        >
                          {t('admin.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {medicines.length === 0 && (
              <p className="text-center py-8 text-slate-500">Aucun médicament trouvé</p>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-4">{medicines.length} médicaments</p>
      </div>
    </div>
  );
};

// Admin Users Management
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const { token, user: currentUser } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      toast.error('Échec du chargement des utilisateurs');
    }
  };

  const handleRoleChange = async (userEmail, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${encodeURIComponent(userEmail)}/role?role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin.userRoleUpdated'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (userEmail) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(userEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin.userDeleted'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la suppression');
    }
  };

  const getRoleBadgeStyle = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-700',
      order_manager: 'bg-blue-100 text-blue-700',
      stock_manager: 'bg-teal-100 text-teal-700',
      analyst: 'bg-yellow-100 text-yellow-700',
      user: 'bg-slate-100 text-slate-700'
    };
    return styles[role] || styles.user;
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Super Admin',
      order_manager: 'Gest. Commandes',
      stock_manager: 'Gest. Stock',
      analyst: 'Analyste',
      user: 'Utilisateur'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-teal-500 hover:underline text-sm mb-2 inline-block">&larr; Retour</Link>
        <h1 className="text-3xl font-bold mb-6">{t('admin.manageUsers')}</h1>

        <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left py-3 px-4 text-slate-700">{t('auth.email')}</th>
                  <th className="text-left py-3 px-4 text-slate-700">{t('auth.fullName')}</th>
                  <th className="text-left py-3 px-4 text-slate-700">{t('admin.role')}</th>
                  <th className="text-left py-3 px-4 text-slate-700">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.email} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-user-row-${user.email}`}>
                    <td className="py-3 px-4 text-slate-900">{user.email}</td>
                    <td className="py-3 px-4 text-slate-900">{user.full_name}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getRoleBadgeStyle(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {user.email !== currentUser.email && currentUser.role === 'admin' && (
                        <div className="flex gap-2 flex-wrap">
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.email, e.target.value)}
                            className="text-xs px-2 py-1 rounded bg-white border border-slate-300 text-slate-700"
                            data-testid={`role-select-${user.email}`}
                          >
                            <option value="user">Utilisateur</option>
                            <option value="order_manager">Gest. Commandes</option>
                            <option value="stock_manager">Gest. Stock</option>
                            <option value="analyst">Analyste</option>
                            <option value="admin">Super Admin</option>
                          </select>
                          <button
                            onClick={() => handleDelete(user.email)}
                            className="text-rose-500 hover:text-rose-400 text-xs"
                            data-testid={`delete-user-${user.email}`}
                          >
                            Supprimer
                          </button>
                        </div>
                      )}
                      {user.email === currentUser.email && (
                        <span className="text-xs text-slate-500">(Vous)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <p className="text-center py-8 text-slate-500">Aucun utilisateur trouvé</p>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-4">{users.length} utilisateurs</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdminRole(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

// Admin Orders Management
const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [previewPrescription, setPreviewPrescription] = useState(null);
  const { token } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let filtered = res.data;
      if (filter !== 'all') {
        filtered = res.data.filter(o => o.order_status === filter || o.payment_status === filter);
      }
      setOrders(filtered);
    } catch (error) {
      toast.error('Échec du chargement des commandes');
    }
  };

  const updateOrderStatus = async (orderId, status, notes = '') => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status, notes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Commande ${status}`);
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const refundOrder = async (orderId) => {
    if (!window.confirm('Confirmer le remboursement ?')) return;
    try {
      await axios.post(`${API}/admin/orders/${orderId}/refund`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Commande remboursée');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const verifyPrescription = async (orderId, verified) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/prescription/verify?verified=${verified}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Ordonnance ${verified ? 'validée' : 'rejetée'}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      validated: 'bg-blue-100 text-blue-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-teal-100 text-teal-700',
      cancelled: 'bg-rose-100 text-rose-700',
      suspended: 'bg-orange-100 text-orange-700',
      refunded: 'bg-slate-100 text-slate-700',
      paid: 'bg-teal-100 text-teal-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-teal-600 hover:underline text-sm mb-2 inline-block">&larr; Retour</Link>
        <h1 className="text-3xl font-bold mb-6 text-slate-900">Gestion des Commandes</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'pending', 'validated', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f ? 'bg-teal-500 text-white' : 'bg-white text-slate-600 border border-slate-300 hover:border-teal-500'
              }`}
            >
              {f === 'all' ? 'Toutes' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 text-slate-600">ID</th>
                  <th className="text-left py-3 px-4">Client</th>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-left py-3 px-4">Total</th>
                  <th className="text-left py-3 px-4">Paiement</th>
                  <th className="text-left py-3 px-4">Statut</th>
                  <th className="text-left py-3 px-4">Ordonnance</th>
                  <th className="text-left py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id} className="border-b border-slate-700 hover:bg-slate-750">
                    <td className="py-3 px-4 font-mono text-xs">{order.id.substring(0, 8)}</td>
                    <td className="py-3 px-4">{order.user_email}</td>
                    <td className="py-3 px-4">{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="py-3 px-4 font-medium">${order.total_amount?.toFixed(2)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(order.payment_status)}`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(order.order_status)}`}>
                        {order.order_status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {order.requires_prescription ? (
                        order.prescription_url ? (
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => setPreviewPrescription(order.prescription_url)} 
                              className="text-teal-500 hover:underline text-xs"
                              data-testid={`view-prescription-${order.id}`}
                            >
                              Voir
                            </button>
                            {order.prescription_status !== 'verified' && (
                              <>
                                <button onClick={() => verifyPrescription(order.id, true)} className="text-green-500 hover:text-green-400 text-xs">✓</button>
                                <button onClick={() => verifyPrescription(order.id, false)} className="text-rose-500 hover:text-rose-400 text-xs">✗</button>
                              </>
                            )}
                            {order.prescription_status === 'verified' && (
                              <span className="text-green-500 text-xs">✓ Validée</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-rose-400 text-xs">Manquante</span>
                        )
                      ) : (
                        <span className="text-slate-500 text-xs">N/A</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 flex-wrap">
                        {order.order_status === 'pending' && (
                          <button onClick={() => updateOrderStatus(order.id, 'validated')} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Valider</button>
                        )}
                        {order.order_status === 'validated' && (
                          <button onClick={() => updateOrderStatus(order.id, 'processing')} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Traiter</button>
                        )}
                        {order.order_status === 'processing' && (
                          <button onClick={() => updateOrderStatus(order.id, 'shipped')} className="px-2 py-1 bg-purple-600 text-white rounded text-xs">Expédier</button>
                        )}
                        {order.order_status === 'shipped' && (
                          <button onClick={() => updateOrderStatus(order.id, 'delivered')} className="px-2 py-1 bg-teal-600 text-white rounded text-xs">Livré</button>
                        )}
                        {!['cancelled', 'refunded', 'delivered'].includes(order.order_status) && (
                          <>
                            <button onClick={() => updateOrderStatus(order.id, 'suspended')} className="px-2 py-1 bg-orange-600 text-white rounded text-xs">Suspendre</button>
                            <button onClick={() => updateOrderStatus(order.id, 'cancelled')} className="px-2 py-1 bg-rose-600 text-white rounded text-xs">Annuler</button>
                          </>
                        )}
                        {order.payment_status === 'paid' && order.order_status !== 'refunded' && (
                          <button onClick={() => refundOrder(order.id)} className="px-2 py-1 bg-slate-600 text-white rounded text-xs">Rembourser</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-4">{orders.length} commandes</p>
      </div>

      {/* Prescription Preview Modal */}
      {previewPrescription && (
        <PrescriptionPreviewModal 
          imageUrl={previewPrescription} 
          onClose={() => setPreviewPrescription(null)} 
        />
      )}
    </div>
  );
};

// Admin Analytics Dashboard
const AdminAnalytics = () => {
  const [salesData, setSalesData] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [inventoryAlerts, setInventoryAlerts] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [period, setPeriod] = useState('week');
  const { token } = useAuth();

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      const [sales, products, inventory, categories] = await Promise.all([
        axios.get(`${API}/admin/analytics/sales?period=${period}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/analytics/top-products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/analytics/inventory-alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/analytics/category-breakdown`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSalesData(sales.data);
      setTopProducts(products.data);
      setInventoryAlerts(inventory.data);
      setCategoryBreakdown(categories.data);
    } catch (error) {
      toast.error('Échec du chargement des analytics');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-teal-600 hover:underline text-sm mb-2 inline-block">&larr; Retour</Link>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Analytics & Ventes</h1>
          <div className="flex gap-2">
            {['day', 'week', 'month', 'year'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-lg text-sm ${period === p ? 'bg-teal-500 text-white' : 'bg-white text-slate-600 border border-slate-300'}`}
              >
                {p === 'day' ? 'Jour' : p === 'week' ? 'Semaine' : p === 'month' ? 'Mois' : 'Année'}
              </button>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        {salesData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="stat-card success">
              <p className="text-sm text-slate-500 mb-1">Chiffre d'affaires</p>
              <p className="text-3xl font-bold text-teal-600">${salesData.total_revenue}</p>
              <p className={`text-sm ${salesData.revenue_growth >= 0 ? 'text-green-600' : 'text-rose-600'}`}>
                {salesData.revenue_growth >= 0 ? '↑' : '↓'} {Math.abs(salesData.revenue_growth)}%
              </p>
            </div>
            <div className="stat-card">
              <p className="text-sm text-slate-500 mb-1">Commandes</p>
              <p className="text-3xl font-bold text-slate-900">{salesData.total_orders}</p>
            </div>
            <div className="stat-card">
              <p className="text-sm text-slate-500 mb-1">Panier moyen</p>
              <p className="text-3xl font-bold text-slate-900">${salesData.avg_order_value}</p>
            </div>
            <div className={`stat-card ${inventoryAlerts?.out_of_stock_count > 0 ? 'danger' : inventoryAlerts?.low_stock_count > 0 ? 'warning' : ''}`}>
              <p className="text-sm text-slate-500 mb-1">Alertes stock</p>
              <p className="text-3xl font-bold text-rose-600">{inventoryAlerts?.low_stock_count || 0}</p>
              <p className="text-xs text-slate-500">{inventoryAlerts?.out_of_stock_count || 0} en rupture</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Sales Chart */}
          <div className="chart-container">
            <h3 className="text-lg font-bold mb-4">Évolution des ventes</h3>
            {salesData?.daily_breakdown && (
              <div className="space-y-2">
                {salesData.daily_breakdown.slice(-7).map((day, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="text-xs text-slate-500 w-20">{day.date.slice(5)}</span>
                    <div className="flex-1 bg-slate-200 rounded-full h-6 overflow-hidden">
                      <div 
                        className="bg-teal-500 h-full rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${Math.min((day.revenue / (salesData.total_revenue || 1)) * 700, 100)}%` }}
                      >
                        <span className="text-xs text-white font-medium">${day.revenue}</span>
                      </div>
                    </div>
                    <span className="text-xs text-slate-600 w-16">{day.orders} cmd</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Category Breakdown */}
          <div className="chart-container">
            <h3 className="text-lg font-bold mb-4">Ventes par catégorie</h3>
            <div className="space-y-3">
              {categoryBreakdown.slice(0, 8).map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm truncate flex-1">{cat.category}</span>
                  <span className="text-sm font-medium text-teal-500 ml-4">${cat.revenue}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Products */}
          <div className="chart-container">
            <h3 className="text-lg font-bold mb-4">Produits les plus vendus</h3>
            <div className="space-y-3">
              {topProducts.slice(0, 10).map((product, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs text-slate-700">{idx + 1}</span>
                    <span className="text-sm truncate max-w-xs text-slate-900">{product.medicine_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-teal-600">${product.total_revenue?.toFixed(2)}</p>
                    <p className="text-xs text-slate-500">{product.total_quantity} unités</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Inventory Alerts */}
          <div className="chart-container">
            <h3 className="text-lg font-bold mb-4">Alertes d'inventaire</h3>
            {inventoryAlerts?.out_of_stock?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-rose-500 font-medium mb-2">En rupture de stock</p>
                {inventoryAlerts.out_of_stock.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1 text-sm">
                    <span className="truncate">{item.name}</span>
                    <span className="text-rose-500">0</span>
                  </div>
                ))}
              </div>
            )}
            {inventoryAlerts?.low_stock?.length > 0 && (
              <div>
                <p className="text-sm text-yellow-500 font-medium mb-2">Stock faible (&lt; 20)</p>
                {inventoryAlerts.low_stock.filter(i => i.stock_quantity > 0).slice(0, 10).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1 text-sm">
                    <span className="truncate">{item.name}</span>
                    <span className="text-yellow-500">{item.stock_quantity}</span>
                  </div>
                ))}
              </div>
            )}
            {(!inventoryAlerts?.low_stock?.length && !inventoryAlerts?.out_of_stock?.length) && (
              <p className="text-slate-500 text-center py-8">Aucune alerte</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Virtual Pharmacist Chat Widget
const PharmacistChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/pharmacist/chat`, {
        message: text,
        session_id: sessionId
      });
      
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      toast.error('Erreur de communication avec le pharmacien');
      setMessages(prev => [...prev, { role: 'assistant', content: "Désolé, je rencontre un problème technique. Veuillez réessayer." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const toggleRecording = () => {
    if (isRecording) {
      setIsRecording(false);
      // Voice recording would be stopped here
      toast.info('Fonctionnalité vocale bientôt disponible');
    } else {
      setIsRecording(true);
      toast.info('Parlez maintenant...');
      // Voice recording would start here
      setTimeout(() => setIsRecording(false), 3000);
    }
  };

  return (
    <div className="pharmacist-widget">
      {isOpen && (
        <div className="pharmacist-chat" data-testid="pharmacist-chat">
          <div className="pharmacist-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-white">Pharmacien Virtuel</p>
                <p className="text-xs text-white/70">En ligne</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="pharmacist-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 mx-auto text-teal-500 mb-4" />
                <p className="text-slate-400 text-sm">Bonjour ! Je suis votre pharmacien virtuel.</p>
                <p className="text-slate-500 text-xs mt-2">Décrivez vos symptômes ou posez une question.</p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                {msg.content}
              </div>
            ))}
            {loading && (
              <div className="message assistant">
                <div className="flex items-center gap-2">
                  <div className="animate-pulse flex gap-1">
                    <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                    <div className="w-2 h-2 bg-teal-500 rounded-full animation-delay-200"></div>
                    <div className="w-2 h-2 bg-teal-500 rounded-full animation-delay-400"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="pharmacist-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Décrivez vos symptômes..."
              className="flex-1 px-4 py-2 rounded-lg"
              disabled={loading}
              data-testid="pharmacist-input"
            />
            <button
              type="button"
              onClick={toggleRecording}
              className={`voice-btn ${isRecording ? 'recording' : ''}`}
              data-testid="voice-btn"
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="voice-btn !bg-teal-500 !border-teal-500 !text-white"
              data-testid="send-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="pharmacist-btn"
        data-testid="pharmacist-toggle-btn"
      >
        {isOpen ? <X className="w-7 h-7 text-white" /> : <MessageCircle className="w-7 h-7 text-white" />}
      </button>
    </div>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Navbar />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/medicines" element={<ProtectedRoute adminOnly><AdminMedicines /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute adminOnly><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/order-success" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          </Routes>
          <PharmacistChat />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
